import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '../../../../Rumblefish23Theme';
import {
  StyledAvatar,
  StyledCaptionStack,
  StyledCard,
  StyledDot,
  StyledDots,
  StyledFooterStack,
  StyledName,
  StyledNavRow,
  StyledPosition,
  StyledQuote,
  StyledQuoteGroup,
  StyledSliderWrapper,
  StyledStage,
  StyledText,
} from './styles';
import { TestimonialsSliderProps } from './TestimonialsSlider.types';

const VISIBLE_OFFSET = 2;
const AUTOPLAY_MS = 8000;

const positionClassNames: Record<number, string> = {
  0: 'center',
  1: 'right',
  [-1]: 'left',
  2: 'farRight',
  [-2]: 'farLeft',
};

const TestimonialsSlider = ({
  testimonialsData,
}: TestimonialsSliderProps): React.ReactElement | null => {
  const count = testimonialsData?.length || 0;
  const [currentIndex, setCurrentIndex] = useState(0);

  const goTo = useCallback(
    (index: number) => {
      if (count === 0) return;
      setCurrentIndex(((index % count) + count) % count);
    },
    [count]
  );

  const handlePrev = useCallback(
    () => goTo(currentIndex - 1),
    [goTo, currentIndex]
  );
  const handleNext = useCallback(
    () => goTo(currentIndex + 1),
    [goTo, currentIndex]
  );

  useEffect(() => {
    if (count <= 1) return undefined;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % count);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [count, currentIndex]);

  const cards = useMemo(() => {
    if (count === 0) return [];
    return testimonialsData
      .map((testimonial, index) => {
        let offset = index - currentIndex;
        if (offset > count / 2) offset -= count;
        if (offset < -count / 2) offset += count;
        return { testimonial, index, offset };
      })
      .filter((card) => Math.abs(card.offset) <= VISIBLE_OFFSET);
  }, [testimonialsData, currentIndex, count]);

  if (count === 0) return null;

  return (
    <StyledSliderWrapper>
      <StyledStage>
        {cards.map(({ testimonial, index, offset }) => {
          const name = testimonial?.author?.Name || '';
          const positionInCompany = testimonial?.author?.Position || '';
          const avatarSrc = testimonial?.author?.Avatar?.url || '';
          const isCentered = offset === 0;

          return (
            <StyledCard
              key={index}
              className={positionClassNames[offset]}
              onClick={isCentered ? undefined : () => goTo(index)}
              onKeyDown={
                isCentered
                  ? undefined
                  : (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        goTo(index);
                      }
                    }
              }
              role={isCentered ? undefined : 'button'}
              tabIndex={isCentered ? undefined : 0}
              aria-label={
                isCentered ? undefined : `Show testimonial from ${name}`
              }>
              <StyledQuoteGroup>
                <StyledQuote variant="display_lg">“</StyledQuote>
                <StyledText variant="strong_500">
                  {testimonial?.body || ''}
                </StyledText>
              </StyledQuoteGroup>
              <StyledFooterStack>
                <StyledAvatar
                  src={avatarSrc}
                  alt={`${name}_TestimonialAvatar`}
                />
                <StyledCaptionStack>
                  <StyledName variant="strong_500">{name}</StyledName>
                  <StyledPosition variant="regular_100">
                    {positionInCompany}
                  </StyledPosition>
                </StyledCaptionStack>
              </StyledFooterStack>
            </StyledCard>
          );
        })}
      </StyledStage>
      {count > 1 && (
        <StyledNavRow>
          <Button
            variant="flat"
            iconDirection="left"
            aria-label="previous opinion"
            onClick={handlePrev}
          />
          <StyledDots>
            {testimonialsData.map((_, index) => (
              <StyledDot
                key={index}
                type="button"
                aria-label={`Go to testimonial ${index + 1}`}
                active={index === currentIndex}
                onClick={() => goTo(index)}
              />
            ))}
          </StyledDots>
          <Button
            variant="flat"
            aria-label="next opinion"
            onClick={handleNext}
          />
        </StyledNavRow>
      )}
    </StyledSliderWrapper>
  );
};

TestimonialsSlider.displayName = 'TestimonialsSlider';
export default TestimonialsSlider;
